import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Button, DateBox, Lookup, SelectBox, NumberBox, TextArea } from 'devextreme-react'
import { DropDownOptions, Button as SelectBoxButton } from 'devextreme-react/select-box'
import { Popup } from 'devextreme-react/popup'
import notify from 'devextreme/ui/notify'
import { useQuery } from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Internal imports
import { ArraySourceComposer, useLookupDataSource } from '@/auth/api/connector'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useAzureManager } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import { attivita_lavorativa, causale_centro_costo, centro_costo, user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { defaultCRUDAllowedRoles } from '@/routes/hours/HourEditor.route'
import { isUserRoleAllowed } from '@/routes/utils'
import { useScreenSize } from '@/themes/media-query'
import HourEditorFormExpensesGridMemoed from '@/routes/hours/HourEditor.expenses'
import { createQueryBuilderV4 } from '@odata2ts/odata-query-builder'
import {
  qazienda,
  qcausale_centro_costo,
  qcentro_costo,
  qpiattaforma_attivita_lavorativa,
  qsede,
  qtask,
  quser,
} from '@/model/qsadminapi/QQsAdminApiModule'
import { DurationInput } from '../../components/InputComponents/DurationInput'

import './HourEditor.formV2.scss'
import {
  Form as DXForm,
  ButtonOptions,
  CustomRule,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
  ColCountByScreen,
  FormRef,
} from 'devextreme-react/form'
import { Validator } from 'devextreme-react/validator'
import { ValidationCallbackData } from 'devextreme/common'

// Interface for causale lookup items
interface CausaleItem {
  id: number
  nome: string
  note: string
}

export interface HourEditorFormV2Props {
  creating?: boolean
  hour?: attivita_lavorativa
  onHourChange?: (hour: attivita_lavorativa) => void
  onDelete?: (hourId: number) => void
  isExpanded?: boolean
  onToggleExpand?: () => void
}

export const HourEditorFormV2: React.FC<HourEditorFormV2Props> = ({
  creating = false,
  hour,
  onHourChange,
  onDelete,
  isExpanded,
  onToggleExpand,
}) => {
  const { userInfo } = useAzureManager()
  const formRef = React.useRef<FormRef>(null)
  const [selectedAziendaId, setSelectedAziendaId] = useState<number | null>(
    hour?.task?.sede?.azienda?.id || hour?.sede?.azienda?.id || null,
  )
  const [isTaskSelection, setIsTaskSelection] = useState(false)
  const [spesePopupVisible, setSpesePopupVisible] = useState(false)
  const handleUpdate = useCallback(
    (updates: Partial<attivita_lavorativa>) => {
      const newHour = { ...(hour || {}), ...updates } as attivita_lavorativa
      if (onHourChange) {
        onHourChange(newHour)
      }
      return newHour
    },
    [hour, onHourChange],
  )

  const isRoleAllowed = useMemo(() => isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles), [userInfo?.roles])
  const isAdmin = useMemo(
    () => isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator]),
    [userInfo?.roles],
  )
  const currentScreenSize = useScreenSize()

  const { minDate, maxDate } = useMemo(() => {
    const currentDate = hour?.data ? new Date(hour.data) : new Date()
    const day = currentDate.getDay()
    const diffToMonday = day === 0 ? -6 : 1 - day // Sunday is 0, Monday is 1

    const monday = new Date(currentDate)
    monday.setDate(currentDate.getDate() + diffToMonday)

    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    return { minDate: monday, maxDate: sunday }
  }, [hour?.data])

  // Data sources
  const { dataSource: impiegatoDataSource, isLoading: isImpiegatoLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('user', quser)
      .expanding('reparto', (repartoBuilder, qreparto) => {
        repartoBuilder.select('id', 'nome')
      })
      .select('id', 'fullname', 'email', 'reparto')
      .build(),
  )
  const { dataSource: centroCostoDataSource, isLoading: isCentroCostoLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('centro_costo', qcentro_costo)
      .filter(
        hour?.impiegato?.reparto
          ? qcentro_costo.visibility
              .any((ccVisibility) => ccVisibility.reparto.props.id.eq(Number(hour.impiegato?.reparto?.id)))
              .or(qcentro_costo.visibility.any((ccVisibility) => ccVisibility.reparto.props.id.eq(null)))
          : null,
      )
      .expanding('causali_centro_costo', (qCausali) => qCausali.select('id', 'nome'))
      .orderBy(qcentro_costo.nome.asc())
      .select('id', 'nome')
      .build(),
  )
  const causaleFilter = useCallback(
    (item: any) => {
      return item.centro_costo?.id === hour?.centro_costo?.id
    },
    [hour?.centro_costo?.id],
  )
  const { dataSource: causaleDataSource, isLoading: isCausaleLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('causale_centro_costo', qcausale_centro_costo)
      .select('id', 'nome', 'note')
      .expanding('centro_costo', (qCentroCosto) => qCentroCosto.select('id', 'nome'))
      .build(),
    causaleFilter,
  )
  const { dataSource: piattaformaDataSource, isLoading: isPiattaformaLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('piattaforma_attivita_lavorativa', qpiattaforma_attivita_lavorativa)
      .select('id', 'nome')
      .build(),
  )
  const { dataSource: aziendaDataSource, isLoading: isAziendaLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('azienda', qazienda).select('id', 'nome').build(),
  )

  const sedeFilter = useCallback(
    (item: any) => {
      // console.log('Sede filter callback triggered with item:', item)
      return selectedAziendaId ? item.azienda?.id === selectedAziendaId : true
    },
    [selectedAziendaId],
  )
  const { dataSource: sedeDataSource, isLoading: isSedeLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('sede', qsede)
      .select('id', 'nome')
      .expanding('azienda', (qAzienda) => qAzienda.select('id', 'nome'))
      .build(),
    sedeFilter,
  )

  const taskFilter = useCallback(
    (item: any) => {
      if (selectedAziendaId) {
        if (hour?.sede?.id) {
          return item.sede?.id === hour.sede.id
        }
        return item.sede?.azienda?.id === selectedAziendaId
      }
      return true
    },
    [hour?.sede?.id, selectedAziendaId],
  )

  const { dataSource: taskDataSource, isLoading: isTaskLoading } = useLookupDataSource(
    'id',
    createQueryBuilderV4('task', qtask)
      .select('id', 'ded_Dis')
      .expanding('sede', (qSede) =>
        qSede.select('id', 'nome').expanding('azienda', (qAzienda) => qAzienda.select('id', 'nome')),
      )
      .build(),
    taskFilter,
  )

  const interventoDataSource = useMemo(
    () => (hour?.intervento ? ArraySourceComposer('id', [hour.intervento]) : ArraySourceComposer('id', [])),
    [hour?.intervento],
  )

  useEffect(() => {
    const getCurrentUser = async () => {
      const userMail = userInfo?.email
      if (creating && userMail && !hour?.impiegato?.id) {
        const response = await useQsAdminApiManager
          .getState()
          .service.user()
          .query((builder, q) =>
            builder
              .expanding('reparto', (repartoBuilder, qreparto) => {
                repartoBuilder.select('id', 'nome')
              })
              .select('id', 'fullname', 'email', 'reparto')
              .filter(q.email.eq(userMail)),
          )
        if (response.data.value.length === 1) {
          handleUpdate({ impiegato: response.data.value[0] as user })
        } else {
          notify("Impossibile caricare l'impiegato corrente", 'error')
        }
      }
    }
    getCurrentUser()
  }, [handleUpdate, hour?.impiegato?.id, userInfo?.email, creating])
  const size = useScreenSize()
  return (
    <>
      <form className="hour-ediactionstor-form">
        <DXForm
          id={'hour_form'}
          formData={hour}
          ref={formRef}
          labelLocation={'top'}
          showValidationSummary={true}
          readOnly={!isRoleAllowed}
        >
          <ColCountByScreen xs={2} sm={2} md={3} lg={3} />
          <GroupItem colSpan={2}>
            <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
            <GroupItem colSpan={size.isLarge || size.isMedium ? 2 : 1}>
              <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
              <GroupItem caption="Informazioni Generali">
                <ColCountByScreen xs={2} sm={2} md={1} lg={1} />
                <SimpleItem>
                  <Label text="Impiegato" />
                  <Lookup
                    value={hour?.impiegato?.id}
                    dataSource={impiegatoDataSource}
                    displayExpr="fullname"
                    valueExpr="id"
                    disabled={isImpiegatoLoading}
                    placeholder="Seleziona impiegato..."
                    searchEnabled={true}
                    dropDownCentered={true}
                    readOnly={!isAdmin}
                    showClearButton={true}
                    onValueChanged={(e) => {
                      handleUpdate({ impiegato: { id: e.value } as any })
                    }}
                  >
                    <DropDownOptions showTitle={true} title="Selezionare l'impiegato" hideOnOutsideClick={true} />
                    <Validator>
                      <RequiredRule message="L'impiegato è obbligatorio" />
                    </Validator>
                  </Lookup>
                </SimpleItem>

                <SimpleItem>
                  <Label text="Data" />
                  <DateBox
                    value={hour?.data ?? new Date()}
                    displayFormat="dd/MM/yyyy"
                    min={minDate}
                    max={maxDate}
                    type="date"
                    pickerType="calendar"
                    applyValueMode="instantly"
                    onValueChanged={(e) => {
                      handleUpdate({ data: e.value })
                    }}
                  >
                    <Validator>
                      <RequiredRule message="La data è obbligatoria" />
                    </Validator>
                  </DateBox>
                </SimpleItem>

                <SimpleItem>
                  <Label text="Azienda" />
                  <SelectBox
                    value={selectedAziendaId}
                    dataSource={aziendaDataSource}
                    displayExpr="nome"
                    valueExpr="id"
                    placeholder="Seleziona l'azienda..."
                    searchEnabled={true}
                    disabled={isAziendaLoading}
                    showClearButton={true}
                    onValueChanged={(e) => {
                      setSelectedAziendaId(e.value)
                      if (!e.value) handleUpdate({ tempo_trasferta: 'PT0S', tempo_cliente: 'PT0S' })
                    }}
                  >
                    <DropDownOptions
                      showTitle={true}
                      title="Selezionare l'azienda"
                      hideOnOutsideClick={true}
                      width={currentScreenSize.isLarge ? '150%' : ''}
                      resizeEnabled={currentScreenSize.isLarge}
                    />
                    <SelectBoxButton name="clear" location="after">
                      <ButtonOptions
                        disabled={isAziendaLoading}
                        onClick={() => {
                          setSelectedAziendaId(null)
                        }}
                      />
                    </SelectBoxButton>
                    <SelectBoxButton name="dropDown" location="after" />
                  </SelectBox>
                </SimpleItem>
              </GroupItem>
              <GroupItem caption="Classificazione">
                <ColCountByScreen xs={1} sm={1} md={1} lg={1} />
                <SimpleItem>
                  <Label text="Centro Costo" />
                  <Lookup
                    value={hour?.centro_costo?.id ?? undefined}
                    dataSource={centroCostoDataSource}
                    displayExpr="nome"
                    valueExpr="id"
                    placeholder="Seleziona centro di costo..."
                    searchEnabled={true}
                    disabled={isCentroCostoLoading}
                    dropDownCentered={true}
                    onValueChanged={(e) => {
                      const costCenter = e.component.option('selectedItem') as centro_costo | undefined
                      handleUpdate({
                        centro_costo: costCenter,
                        causale: null,
                      })
                      console.log('new object is ', hour)
                    }}
                  >
                    <DropDownOptions
                      showTitle={true}
                      title="Selezionare il centro di costo"
                      hideOnOutsideClick={true}
                      disabled={isCentroCostoLoading}
                    />
                    <Validator>
                      <RequiredRule message="Il centro di costo è obbligatorio" />
                    </Validator>
                  </Lookup>
                </SimpleItem>

                {hour?.centro_costo &&
                  hour.centro_costo.causali_centro_costo &&
                  hour.centro_costo.causali_centro_costo.length > 0 && (
                    <SimpleItem>
                      <Label text="Causale" />
                      <Lookup
                        value={hour?.causale?.id ?? null}
                        dataSource={causaleDataSource}
                        displayExpr={(item: CausaleItem) => (item ? `${item.nome} - ${item.note}` : '')}
                        valueExpr="id"
                        placeholder="Seleziona la causale..."
                        searchEnabled={true}
                        dropDownCentered={true}
                        showClearButton={true}
                        disabled={isCausaleLoading}
                        onValueChanged={(e) => {
                          const causaleObject = e.component.option('selectedItem') as CausaleItem | undefined
                          handleUpdate({ causale: causaleObject })
                        }}
                      >
                        <DropDownOptions showTitle={true} title="Selezionare la causale" hideOnOutsideClick={true} />
                        {/* <Validator>
                        <CustomRule
                          message={'Causale obbligatoria'}
                          validationCallback={({ value }: ValidationCallbackData) => {
                            return (value !== null && value !== undefined) || causaleDataSource.totalCount() === 0
                          }}
                        />
                      </Validator> */}
                      </Lookup>
                    </SimpleItem>
                  )}

                <SimpleItem>
                  <Label text="Piattaforma" />
                  <Lookup
                    value={hour?.piattaforma?.id ?? null}
                    dataSource={piattaformaDataSource}
                    displayExpr="nome"
                    valueExpr="id"
                    placeholder="Seleziona piattaforma..."
                    searchEnabled={true}
                    dropDownCentered={true}
                    disabled={isPiattaformaLoading}
                    showClearButton={true}
                    onValueChanged={(e) => {
                      handleUpdate({ piattaforma: { id: e.value } as any })
                    }}
                  >
                    <DropDownOptions showTitle={true} title="Selezionare la piattaforma" hideOnOutsideClick={true} />
                  </Lookup>
                </SimpleItem>
              </GroupItem>
            </GroupItem>

            <GroupItem colSpan={size.isLarge || size.isMedium ? 2 : 1}>
              <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
              <GroupItem caption="Sede e Attività" colCount={1}>
                {selectedAziendaId && (
                  <SimpleItem>
                    <Label text="Sede" />
                    <SelectBox
                      value={hour?.sede?.id ?? null}
                      dataSource={sedeDataSource}
                      displayExpr="nome"
                      placeholder="Seleziona la sede..."
                      searchEnabled={true}
                      disabled={isSedeLoading && !selectedAziendaId}
                      showDropDownButton={true}
                      readOnly={!isRoleAllowed}
                      onValueChanged={(e) => {
                        const sede = e.component.option('selectedItem')
                        if (sede) {
                          setSelectedAziendaId(sede.azienda?.id)
                          handleUpdate({ sede: sede, task: null })
                        } else {
                          handleUpdate({ sede: null, task: null })
                        }
                      }}
                    >
                      <DropDownOptions showTitle={true} title="Selezionare la sede" hideOnOutsideClick={true} />
                    </SelectBox>
                  </SimpleItem>
                )}
                <SimpleItem>
                  <Label text="Task" />
                  <Lookup
                    value={hour?.task?.id ?? null}
                    dataSource={taskDataSource}
                    displayExpr="ded_Dis"
                    placeholder="Seleziona il task..."
                    searchEnabled={true}
                    disabled={isTaskLoading}
                    showClearButton={true}
                    onValueChanged={(e) => {
                      const task = e.component.option('selectedItem')
                      if (task) {
                        setIsTaskSelection(true)
                        setSelectedAziendaId(task.sede?.azienda?.id)
                        handleUpdate({ task: task, sede: task.sede, ded_Dis: task.ded_Dis })
                        setTimeout(() => setIsTaskSelection(false), 0)
                      } else {
                        handleUpdate({ task: null, ded_Dis: `new_activity_${hour?.id}` })
                      }
                    }}
                  >
                    <DropDownOptions showTitle={true} title="Selezionare il task" hideOnOutsideClick={true} />
                  </Lookup>
                </SimpleItem>

                {hour?.intervento && (
                  <SimpleItem>
                    <Label text="Intervento" />
                    <Lookup
                      value={hour?.intervento?.id ?? null}
                      dataSource={interventoDataSource}
                      displayExpr="ded_Dis"
                      placeholder="Seleziona intervento..."
                      searchEnabled={true}
                      showClearButton={true}
                      readOnly={true}
                    >
                      <DropDownOptions showTitle={true} title="Selezionare l'intervento" hideOnOutsideClick={true} />
                    </Lookup>
                  </SimpleItem>
                )}
              </GroupItem>

              <GroupItem caption="Tempistiche e Spese">
                <ColCountByScreen xs={2} sm={2} md={3} lg={3} />
                <GroupItem colSpan={size.isLarge || size.isMedium ? 2 : 1}>
                  <ColCountByScreen xs={1} sm={1} md={1} lg={1} />
                  <SimpleItem>
                    <Label text="Ufficio" />
                    <DurationInput
                      value={hour?.tempo_ufficio || 'PT0S'}
                      onChange={(newValue) => handleUpdate({ tempo_ufficio: newValue })}
                      label="Ufficio"
                      readOnly={!isRoleAllowed}
                    />
                  </SimpleItem>
                  {selectedAziendaId && (
                    <>
                      <SimpleItem>
                        <Label text="Cliente" />
                        <DurationInput
                          value={hour?.tempo_cliente || 'PT0S'}
                          onChange={(newValue) => handleUpdate({ tempo_cliente: newValue })}
                          label="Cliente"
                          readOnly={!isRoleAllowed}
                        />
                      </SimpleItem>
                      <SimpleItem>
                        <Label text="Trasferta" />
                        <DurationInput
                          value={hour?.tempo_trasferta || 'PT0S'}
                          onChange={(newValue) => handleUpdate({ tempo_trasferta: newValue })}
                          label="Trasferta"
                          readOnly={!isRoleAllowed}
                        />
                      </SimpleItem>
                    </>
                  )}
                </GroupItem>
                {selectedAziendaId && (
                  <GroupItem colSpan={1}>
                    <ColCountByScreen xs={1} sm={1} md={1} lg={1} />
                    <SimpleItem>
                      <Label text="Trasferta" />
                      <NumberBox
                        value={hour?.spese_trasferta ?? 0}
                        showSpinButtons={true}
                        min={0}
                        readOnly={true}
                        onValueChanged={(e) => handleUpdate({ spese_trasferta: e.value })}
                      />
                    </SimpleItem>

                    <SimpleItem>
                      <Label text="Vitto" />
                      <NumberBox
                        value={hour?.spese_vitto ?? 0}
                        showSpinButtons={true}
                        min={0}
                        readOnly={true}
                        onValueChanged={(e) => handleUpdate({ spese_vitto: e.value })}
                      />
                    </SimpleItem>

                    <SimpleItem>
                      <Label text="Alloggio" />
                      <NumberBox
                        value={hour?.spese_alloggio ?? 0}
                        showSpinButtons={true}
                        min={0}
                        readOnly={true}
                        onValueChanged={(e) => handleUpdate({ spese_alloggio: e.value })}
                      />
                    </SimpleItem>
                  </GroupItem>
                )}
              </GroupItem>
            </GroupItem>
          </GroupItem>
          <GroupItem caption="Note e Spese" colSpan={size.isLarge || size.isMedium ? 1 : 2}>
            <ColCountByScreen xs={1} sm={1} md={1} lg={1} />
            <SimpleItem>
              <Label text="Note" />
              <TextArea value={hour?.note || ''} height={340} onValueChanged={(e) => handleUpdate({ note: e.value })} />
            </SimpleItem>
            <SimpleItem>
              <Button text="Gestione Spese" onClick={() => setSpesePopupVisible(true)} type="default" />
            </SimpleItem>
            {!creating && onDelete && hour?.id && (
              <SimpleItem>
                <Button
                  text="Elimina"
                  onClick={() => onDelete(hour.id)}
                  type="danger"
                  stylingMode="contained"
                  icon="trash"
                />
              </SimpleItem>
            )}
          </GroupItem>
        </DXForm>
      </form>

      <Popup
        visible={spesePopupVisible}
        onHiding={() => setSpesePopupVisible(false)}
        dragEnabled={true}
        hideOnOutsideClick={true}
        showTitle={true}
        title="Gestione Spese"
        width="60%"
        height="60%"
      >
        {creating ? (
          <p>Per aggiungere spese, prima salva l&apos;ora.</p>
        ) : (
          <HourEditorFormExpensesGridMemoed impiegato={hour?.impiegato} data={hour?.data} />
        )}
      </Popup>
    </>
  )
}
